import apiClient from "../lib/api-client";

export interface DiscountScheme {
  id: string;
  name: string;
  code?: string;
  description?: string;
  discountType: string;
  appliesTo?: "ALL" | "PRODUCT" | "CATEGORY";
  productId?: string | null;
  categoryId?: string | null;
  discountValue: number;
  discountPercent?: number | null;
  discountAmount?: number | null;
  maxDiscountPercent?: number;
  minPurchaseAmount?: number;
  maxDiscountAmount?: number;
  minQuantity?: number;
  buyQuantity?: number;
  getQuantity?: number;
  getProductId?: string | null;
  getPackId?: string | null;
  startDate?: string;
  endDate?: string;
  priority?: number;
  isStackable: boolean;
  isActive: boolean;
  _count?: {
    productDiscounts: number;
    categoryDiscounts: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiscountSchemeData {
  name: string;
  code?: string;
  description?: string;
  discountType: string;
  discountValue: number;
  maxDiscountPercent?: number;
  minPurchaseAmount?: number;
  maxDiscountAmount?: number;
  startDate?: string;
  endDate?: string;
  priority?: number;
  isStackable?: boolean;
  getProductId?: string;
  getPackId?: string;
}

export interface DiscountSchemesParams {
  page?: number;
  limit?: number;
  search?: string;
  discountType?: string;
  isActive?: boolean;
}

export interface DiscountSchemesResponse {
  data: DiscountScheme[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const discountSchemeService = {
  getAll: async (
    params?: DiscountSchemesParams,
  ): Promise<DiscountSchemesResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    if (params?.search) searchParams.set("search", params.search);
    if (params?.discountType)
      searchParams.set("discountType", params.discountType);
    if (params?.isActive !== undefined)
      searchParams.set("isActive", params.isActive.toString());

    const response = await apiClient.get(
      `/discount-schemes?${searchParams.toString()}`,
    );
    return response.data;
  },

  getById: async (id: string): Promise<DiscountScheme> => {
    const response = await apiClient.get(`/discount-schemes/${id}`);
    return response.data;
  },

  getActive: async (): Promise<DiscountScheme[]> => {
    const response = await apiClient.get("/discount-schemes/active");
    return response.data;
  },

  create: async (data: CreateDiscountSchemeData): Promise<DiscountScheme> => {
    const response = await apiClient.post("/discount-schemes", data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  update: async (
    id: string,
    data: Partial<CreateDiscountSchemeData>,
  ): Promise<DiscountScheme> => {
    const response = await apiClient.patch(`/discount-schemes/${id}`, data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/discount-schemes/${id}`, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
  },

  addProductDiscount: async (
    schemeId: string,
    productId: string,
    discountValue: number,
  ): Promise<any> => {
    const response = await apiClient.post(
      `/discount-schemes/${schemeId}/products`,
      {
        productId,
        discountValue,
      },
    );
    return response.data;
  },

  addCategoryDiscount: async (
    schemeId: string,
    categoryId: string,
    discountValue: number,
  ): Promise<any> => {
    const response = await apiClient.post(
      `/discount-schemes/${schemeId}/categories`,
      {
        categoryId,
        discountValue,
      },
    );
    return response.data;
  },
};
